import React from "react";
import { buildFileUrl, getFile } from '@sanity/asset-utils';
import {
    StyledWrapContainer,
    Heading,
    IconList,
    IconText,
    IconImage,
    Icon,
    IconTitle,
    Description,
    StyledVideo,
    TextWithIcons,
    TextBeforeIcons,
} from "./styles";

const sanityConfig = () => ({
    projectId: process.env.GATSBY_SANITY_PROJECT_ID,
    dataset: process.env.GATSBY_SANITY_DATASET,
});

const getFilePath = (node) => {
    if (!node) return;

    const config = sanityConfig();
    return buildFileUrl(getFile(node, config).asset, config);
};

import {
    SectionSettings,
    SanityImage,
    SanityColor,
} from "../../interfaces/sanity";
import { useTheme } from "styled-components";

interface Props {
    data: {
        heading?: {
            text: string;
            textColor: SanityColor;
        };
        description: string;
        textBeforeIcons: string;
        video: any;
        videoShopify: any;
        videoYouTube?: string;
        icons?: {
            enabled: boolean;
            bigIcons: boolean;
            horizontal?: boolean;
            mobileDir?: "row" | "column";
            list: [
                {
                    _key: string;
                    _type: "icon";
                    image: SanityImage;
                    text: string;
                }
            ];
        };
        experiment: {
            variantA?: boolean;
            variantB?: boolean;
        };
        backgroundColor: string | undefined;
        sectionSettings: SectionSettings;
    };
}

const TextVideoIconsSection = ({ data }: Props) => {
    const { icons, heading, description, video, videoShopify, videoYouTube, backgroundColor, textBeforeIcons } = data;
    const theme = useTheme();

    const renderIcons = (
        <IconList mobileDir={icons?.mobileDir} horizontal={icons?.horizontal}>
            {icons?.list.map((icon) => (
                <Icon key={icon._key} horizontal={icons.horizontal}>
                    <IconImage imageRef={icon.image} big={icons.bigIcons} alt={icon?.image?.alt}/>
                    <IconTitle>{icon.title}</IconTitle>
                    <IconText horizontal={icons?.horizontal}>{icon.text}</IconText>
                </Icon>
            ))}
        </IconList>
    
    )

    const getYouTubeEmbedUrl = (url) => {
        if (!url) return "";
        // handle youtu.be short URLs
        const short = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
        if (short) return `https://www.youtube.com/embed/${short[1]}`;
      
        // handle normal URLs
        const normal = url.match(/[?&]v=([a-zA-Z0-9_-]+)/);
        if (normal) return `https://www.youtube.com/embed/${normal[1]}`;
      
        // already embed format
        if (url.includes("embed")) return url;
      
        return url; // fallback
      };
      
    let videoSrc;
    let isYouTube = false;
    
    if (videoShopify) {
        videoSrc = videoShopify;
      } else if (videoYouTube) {
        videoSrc = getYouTubeEmbedUrl(videoYouTube);
        isYouTube = true;
      } else if (video && video.asset) {
        videoSrc = getFilePath(video.asset);
      }

    return (
        <StyledWrapContainer id='videoWithIconsSection' backgroundColor={backgroundColor} className={data?.experiment?.variantB ? 'variant_B' : data?.experiment?.variantA ? 'variant_A': ''}>
            {heading && (
                <Heading
                    color={heading?.textColor?.title}
                >
                    {heading?.text}
                </Heading>
            )}
            {description && <Description>{description}</Description>}
            {videoSrc && (
                isYouTube ? (
                    <StyledVideo
                    as="iframe" 
                    src={videoSrc.replace("watch?v=", "embed/") + "?autoplay=1&mute=1"}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    />
                ) : (
                    <StyledVideo
                    src={videoSrc}
                    autoPlay={true}
                    playsInline={true}
                    controls={true}
                    muted={true}
                    />
                )
            )}

            {(textBeforeIcons || (icons?.enabled && renderIcons)) && (
              <TextWithIcons>
                {textBeforeIcons && <TextBeforeIcons>{textBeforeIcons}</TextBeforeIcons>}
                {icons?.enabled && renderIcons}
              </TextWithIcons>
            )}

        </StyledWrapContainer>
    );
};

export default TextVideoIconsSection;
